import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '@umss/shared/data-access';
import { HttpHelper } from '@umss/shared/utils';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { API_BASE_URL } from '../tokens/api-base-url.token';
import { HURO_APP_ID } from '../tokens/app-id.token';

export const EXCLUDED_ENDPOINTS = [
  'login',
  'resend-verification',
  'verify-email',
  'forgot-password',
  'social-login',
  'refresh-token'
];

export const authInterceptor = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);

  const token = authService.accessToken;
  const apiUrl = inject(API_BASE_URL);
  const huroAppId = inject(HURO_APP_ID);

  // Check apiUrl is defined
  try {
    new URL(apiUrl);
  } catch {
    throw new Error(
      `Invalid API_URL: The API URL provided ${apiUrl} is not a valid URL.`
    );
  }

  const url = request.url;

  // If the request URL does not start with the API URL, proceed without adding the Authorization header
  if (!url.startsWith(apiUrl)) {
    return next(request);
  }

  // Skip adding the Authorization header for login and refresh-token endpoints
  if (
    EXCLUDED_ENDPOINTS.some((excludedUrl) =>
      HttpHelper.isEndpoint(url, excludedUrl)
    )
  ) {
    const updatedRequest = HttpHelper.addMissingHeaders(request, {
      'X-Huro-App': huroAppId
    });
    return next(updatedRequest);
  }

  // If there is no token, proceed without adding the Authorization header
  if (!token || token.trim() === '') {
    return next(request);
  }

  const updatedRequest = HttpHelper.addMissingHeaders(request, {
    Authorization: `Bearer ${token}`,
    'X-Huro-App': huroAppId
  });
  const isLoggedIn = authService.isAuthenticated();

  return next(updatedRequest).pipe(
    catchError((error: HttpErrorResponse) => {
      // If receiving 401 (Unauthorized), attempt to refresh the token
      if (error.status === 401 && isLoggedIn) {
        return authService.refreshToken().pipe(
          switchMap(() => {
            // Resend the original request with the new token
            const newToken = authService.accessToken;
            if (newToken) {
              const retryRequest = HttpHelper.addMissingHeaders(request, {
                Authorization: `Bearer ${newToken}`
              });
              return next(retryRequest);
            }
            return throwError(() => error);
          }),
          catchError((refreshError) => {
            console.error(
              '[AuthInterceptor] Failed to refresh token:',
              refreshError
            );
            authService.handleRefreshFailure(refreshError);
            return throwError(() => refreshError);
          })
        );
      }

      return throwError(() => error);
    })
  );
};
